import { useCallback, useEffect, useState } from "react";
import { RELEASE_NOTES } from "./releaseNotes";
import { fetchLatestReleaseNote } from "../../queries/releaseNotesApi";
import { captureMessage } from "../../lib/utils";

export const seenKey = (version) => `workspace.releaseNote.${version}.seen`;

const hasSeen = (version) => {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(seenKey(version)) === "true"; } catch (_) { return false; }
};
const markSeen = (version) => {
  try { window.localStorage.setItem(seenKey(version), "true"); } catch (_) { /* ignore */ }
};

// A highlight the modal can actually draw. ReleaseNotesModal reads h.icon /
// h.title / h.body / h.tryIt; on a plain string every one of those is
// undefined, so the row renders completely blank. `generate` legitimately emits
// strings (it parses changelog lines and cannot invent the icon/title/body
// split) and the admin editor promotes them -- but if a note ships unpromoted,
// the bundled note is far better than a dialog full of empty rows.
const isRenderableHighlight = (h) =>
  h != null && typeof h === "object" && !Array.isArray(h) &&
  !!(h.icon || h.title || h.body || h.tryIt);

// Shape check, not just presence. The previous version tested only
// `Array.isArray(highlights) && highlights.length`, so a malformed note passed
// and DISPLACED the fallback instead of deferring to it.
const isUsable = (n) =>
  !!(n && n.version && Array.isArray(n.highlights) && n.highlights.length &&
     n.highlights.every(isRenderableHighlight));

// An empty body is the normal steady state on a cell with nothing published --
// latest_published() filters to status=published and rd returns {}. Silence
// there; report only when the API gave us something it meant us to show.
const isEmptyResponse = (n) => !n || (typeof n === "object" && !Object.keys(n).length);

/**
 * Owns the "What's New" open-state. Fetches the latest PUBLISHED release note
 * from the API and falls back to the bundled RELEASE_NOTES constant on
 * empty/error/malformed. Auto-opens once when the resolved note's version is
 * unseen.
 *
 * The fallback is deliberate but it also hides failure: users always get a
 * plausible dialog, so a dead DB path (500, AccessDenied, unpromoted
 * highlights) looks exactly like "nothing published yet". Hence the Sentry
 * signals below -- they are the only way anyone finds out.
 */
export const useReleaseNotes = (authData = null) => {
  const [notes, setNotes] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const apply = (n) => {
      if (cancelled) return;
      const usable = isUsable(n);
      if (!usable && !isEmptyResponse(n)) {
        const highlights = Array.isArray(n?.highlights) ? n.highlights : [];
        captureMessage("Release note unusable, falling back to bundled notes", {
          level: "warning",
          fingerprint: ["release-notes-unusable"],
          tags: {
            noteVersion: n?.version || "(none)",
            highlightCount: highlights.length,
            // The likely cause: published without promoting in the admin editor.
            unpromotedCount: highlights.filter((h) => !isRenderableHighlight(h)).length,
          },
        });
      }
      const resolved = usable ? n : RELEASE_NOTES;
      setNotes(resolved);
      setOpen(!hasSeen(resolved.version));
    };
    fetchLatestReleaseNote(authData)
      .then(apply)
      .catch((err) => {
        captureMessage("Release notes fetch failed, falling back to bundled notes", {
          level: "warning",
          fingerprint: ["release-notes-fetch-failed"],
          tags: { reason: err?.message || String(err) },
        });
        apply(RELEASE_NOTES);
      });
    return () => { cancelled = true; };
  }, [authData]);

  const dismiss = useCallback(() => {
    if (notes) markSeen(notes.version);
    setOpen(false);
  }, [notes]);
  const openNotes = useCallback(() => setOpen(true), []);

  return { open, openNotes, dismiss, notes };
};

export default useReleaseNotes;
